/**
 * "Glass Bar" — designer Cover 2.
 * Same editorial top block as "Elegant", but the bottom chrome is a
 * full-width frosted-glass bar (blurred photo + near-black tint) holding
 * the stats and a "ЛИСТАЙ ДАЛЬШЕ ›" cue.
 *
 * Layout reflows: cleared texts are skipped, and the cue yields when
 * long stats need its space.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SlideStyle } from "../types";
import {
  drawDesignerBackdrop, drawMockScrim, drawWatermark, drawCoverFitImage,
  drawTrackedText, measureTracked, drawStatRow,
  drawChevron, drawSerifHeadline,
} from "../shared";
import { NUMBER_FONT } from "../fonts";
import { fieldDiary } from "./field-diary";

const BAR_TOP = 1120;

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 76;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);
  const textOnLight = drawDesignerBackdrop(ctx, data);
  drawMockScrim(ctx, 550, true, !textOnLight);

  const rgb = textOnLight ? "28,36,32" : "255,255,255";
  const solid = textOnLight ? "#1c2420" : "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // ── Top block (shared design with "Elegant") ──
  const kicker = data.kickerText.trim();
  const kickerSize = 34 * (data.kickerScale ?? 1);
  if (kicker) {
    ctx.font = `500 ${kickerSize}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.58)`;
    drawTrackedText(ctx, kicker.toUpperCase(), 64, 63 + kickerSize, 10 * (data.kickerScale ?? 1));
  }

  const size = 295 * (data.headlineFontSize / 140);
  const capTop = kicker ? 63 + kickerSize + 65 : 96;
  ctx.fillStyle = solid;
  const lastBaseline = drawSerifHeadline(ctx, data.headlineTitle, 64, 0, W - 128, size, DISPLAY, { capTop });

  const subtitle = data.subtitle.trim();
  if (subtitle) {
    const s = data.subtitleScale ?? 1;
    ctx.font = `300 ${46 * s}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.78)`;
    drawTrackedText(ctx, subtitle.toUpperCase(), 64, lastBaseline + 68 * Math.max(s, 0.8), 12 * s);
  }

  // ── Frosted bottom bar ──
  if (data.photo) {
    // Re-draw the photo blurred inside the bar to fake backdrop-filter.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, BAR_TOP, W, H - BAR_TOP);
    ctx.clip();
    ctx.filter = "blur(14px)";
    drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
    ctx.filter = "none";
    ctx.restore();
  }
  ctx.fillStyle = textOnLight ? "rgba(240,244,241,0.86)" : "rgba(5,14,10,0.86)";
  ctx.fillRect(0, BAR_TOP, W, H - BAR_TOP);
  ctx.fillStyle = `rgba(${rgb},0.07)`;
  ctx.fillRect(0, BAR_TOP, W, 1);

  // ── Stats inside the bar ──
  const st = data.statsScale ?? 1;
  const statsEnd = drawStatRow(ctx, data.stats, {
    x0: PAD,
    numBase: 1256,
    labelBase: 1256 + 42 * st,
    numFont: `400 ${72 * st}px ${NUMBER_FONT}`,
    textFont: `400 ${42 * st}px ${DISPLAY}`,
    labelFont: `500 ${24 * st}px ${BODY}`,
    labelTracking: 5 * st,
    labelAlpha: 0.44,
    solid,
    rgb,
    divider: { top: 1203, height: 64, alpha: 0.15 },
    segGap: 48,
  });

  // ── Swipe cue + chevron, right side — yields to long stats ──
  const cue = (data.swipeText?.trim() || "ЛИСТАЙ ДАЛЬШЕ").toUpperCase();
  ctx.font = `500 26px ${BODY}`;
  const cueStart = 1008 - measureTracked(ctx, cue, 8);
  if (statsEnd < cueStart - 40) {
    ctx.fillStyle = `rgba(${rgb},0.38)`;
    drawTrackedText(ctx, cue, cueStart, 1204, 8);
    ctx.strokeStyle = solid;
    drawChevron(ctx, (cueStart + 1008) / 2, 1258, 9, 18, 7);
  }

  drawWatermark(ctx, style, textOnLight);
}

export const glassBar: CarouselTemplate = {
  id: "glass-bar",
  name: "Glass Bar",
  premium: false,
  drawCover,
  drawSpecies: fieldDiary.drawSpecies,
  drawClosing: fieldDiary.drawClosing,
  // Species/closing draw functions are literally reused from Field Diary,
  // so its overlay positions apply unchanged. Cover has its own bespoke
  // layout here — not yet click-to-edit.
  useTextOverlay: { species: true, closing: true },
};
